// Library methods
import { useCallback } from "react";
import { DateTime } from "luxon";

// Hooks
import { useAddEditExamHelpers } from "./useAddEditExamHelpers";
import { useLinkExamDevice } from "src/services/devices/hooks/useLinkExamDevice";
import { useEditExam } from "src/services/exams/hooks/useEditExam";

// Constants
import {
  eyeTypeEnums,
  examCardEnums,
  ExamCardSectionNameType,
} from "src/constants/enums";

// Types
import {
  AddExamSectionType,
  AddLensSectionType,
  SelectedExamCardType,
} from "src/services/exams/types";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useDevicesContext } from "src/contexts/devices/DevicesContext";

export const useEditExams = () => {
  const {
    getLensSimulationSection,
    getVfEyeSection,
    getEyeSection,
    getSectionName,
    isVfMonocularSections,
    isVfBinocularSections,
    isLensSimulationSections,
    getVideoSection,
  } = useAddEditExamHelpers();
  const { clinicInfo } = useClinicContext();
  const { unlinkedDevices } = useDevicesContext();

  const { mutateAsync: editExamMutation } = useEditExam(clinicInfo?.id);
  const { mutateAsync: linkDeviceMutate } = useLinkExamDevice(clinicInfo?.id);

  const { LEFT, RIGHT, BOTH } = eyeTypeEnums;

  const hasMonocularSections = useCallback(
    (selectedExam: SelectedExamCardType) => {
      const { examFieldType, binocular } = selectedExam;
      return (
        isLensSimulationSections(selectedExam) ||
        isVfMonocularSections(selectedExam) ||
        (!binocular &&
          (examFieldType === examCardEnums.COLOR_VISION.algorithmPath ||
            examFieldType ===
              examCardEnums.CONTRAST_SENSITIVITY.algorithmPath ||
            examFieldType === examCardEnums.VISUAL_ACUITY.algorithmPath))
      );
    },
    [isLensSimulationSections, isVfMonocularSections]
  );

  const getMonocularSections = useCallback(
    (selectedExam: SelectedExamCardType) => {
      const { eyeCheckboxes } = selectedExam;

      // array for left and right eye data
      const monocularSections = [];

      if (eyeCheckboxes.right) {
        if (isLensSimulationSections(selectedExam)) {
          monocularSections.push(getLensSimulationSection(RIGHT, selectedExam));
        } else if (isVfMonocularSections(selectedExam)) {
          monocularSections.push(getVfEyeSection(RIGHT, selectedExam));
        } else {
          monocularSections.push(getEyeSection(RIGHT, selectedExam));
        }
      }
      if (eyeCheckboxes.left) {
        if (isLensSimulationSections(selectedExam)) {
          monocularSections.push(getLensSimulationSection(LEFT, selectedExam));
        } else if (isVfMonocularSections(selectedExam)) {
          monocularSections.push(getVfEyeSection(LEFT, selectedExam));
        } else {
          monocularSections.push(getEyeSection(LEFT, selectedExam));
        }
      }

      return monocularSections;
    },
    [
      isVfMonocularSections,
      isLensSimulationSections,
      getLensSimulationSection,
      getVfEyeSection,
      getEyeSection,
      LEFT,
      RIGHT,
    ]
  );

  const getFormattedExamSections = useCallback(
    (selectedExamCards: Array<SelectedExamCardType>) => {
      let examSections: {
        [key in ExamCardSectionNameType]?: Array<
          AddExamSectionType | AddLensSectionType
        >;
      } = {};

      // loop over the exam sections
      selectedExamCards.forEach((selectedExam) => {
        const { examFieldType } = selectedExam;
        const sectionName = getSectionName(selectedExam);

        if (!sectionName) return;

        // videos
        if (examFieldType === examCardEnums.VIDEO.algorithmPath) {
          const videoSection = getVideoSection(selectedExam);
          examSections[sectionName] = [
            ...(examSections[sectionName] ?? []),
            videoSection,
          ];
        }

        // For Lens Simulations
        else if (isLensSimulationSections(selectedExam)) {
          const binocularSection = getLensSimulationSection(BOTH, selectedExam);
          examSections[sectionName] = [
            ...(examSections[sectionName] ?? []),
            binocularSection,
          ];
        }
        // For Visual Field monocular exams and other monocular types
        else if (hasMonocularSections(selectedExam)) {
          const monocularSections = getMonocularSections(selectedExam);
          examSections[sectionName] = [
            ...(examSections[sectionName] ?? []),
            ...monocularSections,
          ];
        }
        // For Visual Field binocular exams (Esterman)
        else if (isVfBinocularSections(selectedExam)) {
          const binocularSections = getVfEyeSection(BOTH, selectedExam);
          examSections[sectionName] = [
            ...(examSections[sectionName] ?? []),
            binocularSections,
          ];
        }
        // For binocular exams
        // (Contrast Sensitivity, Color Vision, Visual Acuity, RAPD)
        // (Deviation and Correspondence, Fusion and Vergence, Stereopsis)
        // (Aniseikonia, which is binocular only -- only eye "Both" is accepted)
        else {
          const binocularSection = getEyeSection(BOTH, selectedExam);
          examSections[sectionName] = [
            ...(examSections[sectionName] ?? []),
            binocularSection,
          ];
        }
      });

      return examSections;
    },
    [
      isVfBinocularSections,
      hasMonocularSections,
      getLensSimulationSection,
      isLensSimulationSections,
      getSectionName,
      getMonocularSections,
      getEyeSection,
      getVfEyeSection,
      getVideoSection,
      BOTH,
    ]
  );

  const editExam = useCallback(
    async ({
      selectedExamCards, // updated exam data
      scheduledDate,
      patientId,
      examId,
      deviceId,
    }: {
      selectedExamCards: Array<SelectedExamCardType>;
      scheduledDate: DateTime;
      patientId: string;
      examId: string;
      deviceId: string | undefined;
    }) => {
      const examSections = getFormattedExamSections(selectedExamCards);
      const examData = {
        scheduledDate: scheduledDate.toUTC().toISO() ?? "",
        ...examSections,
      };
      const newExam = await editExamMutation({
        patientId,
        examId,
        examData,
      });

      // if there is only 1 device available, auto-link the exam to it
      if (newExam && unlinkedDevices?.length === 1) {
        await linkDeviceMutate({
          exam: newExam,
          deviceId: unlinkedDevices?.[0]?.id,
        });
      }
      // else there is a selected device, link the exam to it
      else if (newExam && deviceId) {
        await linkDeviceMutate({
          exam: newExam,
          deviceId,
        });
      }
    },
    [
      editExamMutation,
      unlinkedDevices,
      linkDeviceMutate,
      getFormattedExamSections,
    ]
  );

  return {
    getFormattedExamSections,
    editExam,
  };
};
